/**
 * Holds the document at the top for as long as the tabbed shell is on screen.
 *
 * The shell is as tall as the screen, and installed, the engine believes the
 * viewport is one status bar shorter than that (see appHeight.ts). It follows
 * that the document is scrollable by the difference — about 62px on the phone
 * this was measured on. Nothing asks to scroll it and no screen needs it: the
 * pages inside the shell each scroll their own container. But a stray drag,
 * a rubber-band, or anything else that moves the document takes the tab bar
 * off the bottom of the screen with it and leaves it there, which is the
 * "sometimes it's in the wrong place" this started as.
 *
 * Only inside the shell. The full-screen forms mounted outside it are taller
 * than the viewport on purpose and scroll the document for real — and iOS
 * scrolls it for them, to keep a focused field above the keyboard. Pinning it
 * there would put fields out of reach.
 *
 * Only when installed, for the same reason `--app-height` only follows the
 * viewport when installed: in a browser tab, scrolling the document is how
 * Safari's toolbar gets out of the way, and fighting it would fight the
 * reader.
 */
const isStandalone = () =>
  typeof document !== "undefined" && document.documentElement.hasAttribute("data-standalone");

export const lockDocumentScroll = () => {
  if (typeof window === "undefined" || !isStandalone()) return () => {};

  const pin = () => {
    // Read before writing: an unconditional scrollTo on every scroll event is
    // a write on every frame of a flick, and a fight with anything else that
    // legitimately moves the document.
    if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
  };

  pin();

  // `scroll` on window is the document's own, not an inner container's —
  // those don't bubble and aren't listened for here, so a page scrolling
  // inside the shell is untouched.
  window.addEventListener("scroll", pin, { passive: true });
  window.addEventListener("orientationchange", pin);
  window.addEventListener("pageshow", pin);

  return () => {
    window.removeEventListener("scroll", pin);
    window.removeEventListener("orientationchange", pin);
    window.removeEventListener("pageshow", pin);
  };
};

export default lockDocumentScroll;
